import { create } from 'zustand'

/**
 * A way back from the actions that cannot otherwise be taken back.
 *
 * Delete, archive and discard are one click each, from controls that only
 * appear on hover, in rows that reflow the moment the click lands — so the
 * next row's delete arrives under the cursor. Three independent audits of this
 * app reached the same conclusion, and one of them proved it by permanently
 * deleting two real tasks while auditing.
 *
 * The offer is deliberately short-lived and never blocks. An action that
 * demands confirmation before every archive would be worse than the problem:
 * the whole value of these controls is that they are fast, and a dialog on the
 * common path teaches people to dismiss dialogs.
 *
 * There is no client-side queue behind this. Convex is server-authoritative
 * with no offline buffer, so "undo" is a real inverse mutation and the toast
 * is only the thing that offers it. That also means the window is a courtesy
 * rather than a guarantee: close the tab inside it and the action stands.
 */

/** Long enough to notice and reach, short enough not to linger. */
export const UNDO_WINDOW_MS = 8_000

type Offer = {
  /** What just happened, in the past tense: "Deleted Book Haircut". */
  message: string
  undo: () => void
  /** Distinguishes consecutive offers so a re-offer restarts the timer. */
  id: number
}

type UndoState = {
  offer: Offer | null
  /** Replaces any pending offer: the newest action is the one you meant. */
  present: (message: string, undo: () => void) => void
  /**
   * Clear the offer. Pass an id to clear only that one.
   *
   * Load-bearing: taking something back often offers a way to put it back
   * again — "Moved to Work" then "Back in Personal" — and the undo button was
   * clearing unconditionally *after* running the handler that presented the
   * second offer, so the way forward was destroyed the instant it appeared.
   * Naming the offer being retired means a newer one survives.
   */
  dismiss: (id?: number) => void
}

let nextId = 0

export const useUndoState = create<UndoState>((set) => ({
  offer: null,
  present: (message, undo) => set({ offer: { message, undo, id: nextId++ } }),
  dismiss: (id) =>
    set((state) =>
      id === undefined || state.offer?.id === id ? { offer: null } : {},
    ),
}))

/**
 * Offer a way back from something just done.
 *
 * Call after the action, not before — the offer describes something that has
 * already happened, and presenting it first would be a confirmation dialog
 * wearing a toast's clothes.
 */
export function offerUndo(message: string, undo: () => void): void {
  useUndoState.getState().present(message, undo)
}
